import React, { useRef, useState } from 'react';
import { useReferral } from '../../contexts/ReferralContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { 
    FileCheck, Shield, AlertTriangle, Pen, CheckCircle
} from 'lucide-react';

const CONSENT_TYPES = [
    { id: 'data_sharing', label: 'Consent to share clinical data with receiving institution', required: true },
    { id: 'referral', label: 'Consent to referral to specialist/secondary care', required: true },
    { id: 'treatment_discussion', label: 'Consent to discuss treatment options with specialist team', required: true },
    { id: 'nhs_spine', label: 'Consent to access NHS Spine/Summary Care Record', required: false },
    { id: 'research', label: 'Consent to anonymized data use for service improvement', required: false }
];

export default function ConsentStep() {
    const { patient_consent, updatePatientConsent, patient } = useReferral();
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);

    // Surgical willingness state
    const surgicalWillingness = patient_consent.willing_for_surgery;

    const handleSurgicalWillingnessChange = (value) => {
        updatePatientConsent({ willing_for_surgery: value });
    };

    const handleConsentTypeChange = (typeId, checked) => {
        const currentTypes = patient_consent.consent_types || [];
        if (checked) {
            updatePatientConsent({ consent_types: [...currentTypes, typeId] });
        } else {
            updatePatientConsent({ consent_types: currentTypes.filter(t => t !== typeId) });
        }
    };

    const allRequiredConsents = CONSENT_TYPES
        .filter(c => c.required)
        .every(c => (patient_consent.consent_types || []).includes(c.id));

    const handleMainConsentChange = (checked) => {
        if (checked && allRequiredConsents && patient_consent.signatory_name) {
            updatePatientConsent({ 
                consent_given: true,
                signature_date: new Date().toISOString().split('T')[0]
            });
        } else {
            updatePatientConsent({ consent_given: false, signature_date: null });
        }
    };

    // Canvas signature handling
    const startDrawing = (e) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.strokeStyle = '#0f766e';
        ctx.lineWidth = 2;
        ctx.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
        setHasSignature(true);
        const canvas = canvasRef.current;
        const signature = canvas.toDataURL('image/png');
        updatePatientConsent({ digital_signature: signature });
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
        updatePatientConsent({ digital_signature: null });
    };

    return (
        <div className="space-y-6">
            {/* Surgical Willingness Question - CRITICAL */}
            <Card className={`card-clinical ${
                surgicalWillingness === false 
                    ? 'bg-red-50 border-red-300' 
                    : surgicalWillingness === true 
                        ? 'bg-emerald-50 border-emerald-300'
                        : 'bg-amber-50 border-amber-200'
            }`}>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <AlertTriangle className={`w-5 h-5 ${
                            surgicalWillingness === false ? 'text-red-600' : 'text-amber-600'
                        }`} />
                        Surgical Intervention Readiness
                    </CardTitle>
                    <CardDescription>
                        This question must be answered before the referral can be submitted
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className={`p-4 rounded-lg border-2 ${
                            surgicalWillingness === false 
                                ? 'border-red-300 bg-red-100' 
                                : 'border-amber-200 bg-white'
                        }`}>
                            <p className="font-semibold text-slate-800 mb-4">
                                Is the patient willing to consider surgical intervention at this stage?
                            </p>
                            <div className="flex gap-4">
                                <button
                                    type="button"
                                    onClick={() => handleSurgicalWillingnessChange(true)}
                                    className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all ${
                                        surgicalWillingness === true
                                            ? 'bg-emerald-600 text-white shadow-lg'
                                            : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-emerald-300'
                                    }`}
                                    data-testid="surgical-yes-btn"
                                >
                                    <CheckCircle className={`w-5 h-5 inline mr-2 ${
                                        surgicalWillingness === true ? 'text-white' : 'text-emerald-500'
                                    }`} />
                                    Yes
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleSurgicalWillingnessChange(false)}
                                    className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all ${
                                        surgicalWillingness === false
                                            ? 'bg-red-600 text-white shadow-lg'
                                            : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-red-300'
                                    }`}
                                    data-testid="surgical-no-btn"
                                >
                                    <AlertTriangle className={`w-5 h-5 inline mr-2 ${
                                        surgicalWillingness === false ? 'text-white' : 'text-red-500'
                                    }`} />
                                    No
                                </button>
                            </div>
                        </div>
                        
                        {surgicalWillingness === false && (
                            <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <h4 className="font-semibold text-red-800 mb-1">
                                            Referral Cannot Be Submitted
                                        </h4>
                                        <p className="text-red-700 text-sm">
                                            Patients who are not willing to consider surgical intervention at this stage 
                                            are not appropriate for orthopaedic secondary care referral. Please continue 
                                            conservative management and reconsider referral when the patient is ready 
                                            to discuss surgical options.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {surgicalWillingness === true && (
                            <div className="bg-emerald-100 border border-emerald-300 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-emerald-700 text-sm">
                                        Patient has indicated willingness to consider surgical intervention. 
                                        Please continue with the consent process below.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Information Governance Notice */}
            <Card className="card-clinical bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                            <h4 className="font-semibold text-blue-900 mb-1">
                                NHS Information Governance
                            </h4>
                            <p className="text-sm text-blue-700">
                                Patient consent is required before submitting this referral. 
                                The information provided will be shared with the receiving healthcare provider 
                                in accordance with NHS data protection policies and GDPR.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Consent Types */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <FileCheck className="w-5 h-5 text-teal-600" />
                        Patient Consent
                    </CardTitle>
                    <CardDescription>
                        Please confirm patient consent for the following:
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {CONSENT_TYPES.map((consent) => (
                        <div 
                            key={consent.id}
                            className={`flex items-start space-x-3 p-4 rounded-lg border ${
                                (patient_consent.consent_types || []).includes(consent.id)
                                    ? 'border-teal-500 bg-teal-50'
                                    : 'border-slate-200 bg-white'
                            }`}
                        >
                            <Checkbox
                                id={consent.id}
                                checked={(patient_consent.consent_types || []).includes(consent.id)}
                                onCheckedChange={(checked) => handleConsentTypeChange(consent.id, checked)}
                                data-testid={`consent-${consent.id}`}
                            />
                            <div className="flex-1">
                                <Label 
                                    htmlFor={consent.id} 
                                    className="font-medium text-slate-700 cursor-pointer"
                                >
                                    {consent.label}
                                    {consent.required && (
                                        <span className="text-rose-500 ml-1">*</span>
                                    )}
                                </Label>
                            </div>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Signatory Details */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Pen className="w-5 h-5 text-teal-600" />
                        Signatory Details
                    </CardTitle>
                    <CardDescription>
                        Record who is providing consent on behalf of the patient
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="label-clinical">Signatory Name *</Label>
                            <Input
                                placeholder={`${patient.first_name} ${patient.last_name}` || "Patient name"}
                                value={patient_consent.signatory_name}
                                onChange={(e) => updatePatientConsent({ signatory_name: e.target.value })}
                                className="input-clinical"
                                data-testid="signatory-name-input"
                            />
                            <p className="text-xs text-slate-500">
                                Patient or authorized representative
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label className="label-clinical">Date</Label>
                            <Input
                                type="date"
                                value={patient_consent.signature_date || new Date().toISOString().split('T')[0]}
                                onChange={(e) => updatePatientConsent({ signature_date: e.target.value })}
                                className="input-clinical"
                                data-testid="signature-date-input"
                            />
                        </div>
                    </div>

                    {/* Digital Signature */}
                    <div className="space-y-2">
                        <Label className="label-clinical">Digital Signature (Optional)</Label>
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-4">
                            <canvas
                                ref={canvasRef}
                                width={400}
                                height={150}
                                className="border border-slate-100 rounded bg-white w-full cursor-crosshair"
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                            />
                            <div className="flex justify-between items-center mt-2">
                                <p className="text-xs text-slate-500">
                                    Draw signature with mouse or touch
                                </p>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={clearSignature}
                                    type="button"
                                >
                                    Clear
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Final Confirmation */}
            <Card className={`card-clinical ${
                patient_consent.consent_given 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-amber-50 border-amber-200'
            }`}>
                <CardContent className="pt-6">
                    <div className="flex items-start space-x-3">
                        <Checkbox
                            id="final-consent"
                            checked={patient_consent.consent_given}
                            onCheckedChange={handleMainConsentChange}
                            disabled={!allRequiredConsents || !patient_consent.signatory_name}
                            data-testid="final-consent-checkbox"
                        />
                        <div>
                            <Label 
                                htmlFor="final-consent" 
                                className={`font-medium cursor-pointer ${
                                    patient_consent.consent_given ? 'text-emerald-800' : 'text-amber-800'
                                }`}
                            >
                                I confirm that informed consent has been obtained from the patient 
                                (or their authorized representative) for this referral and the sharing 
                                of their clinical information.
                            </Label>
                            
                            {!allRequiredConsents && (
                                <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                                    <AlertTriangle className="w-4 h-4" />
                                    Please select all required consent options above
                                </p>
                            )}
                            
                            {!patient_consent.signatory_name && allRequiredConsents && (
                                <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                                    <AlertTriangle className="w-4 h-4" />
                                    Please enter the signatory name
                                </p>
                            )}
                            
                            {patient_consent.consent_given && (
                                <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
                                    <CheckCircle className="w-4 h-4" />
                                    Consent recorded - you may proceed with the referral
                                </p>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
